import { ChangeDetectionStrategy, Component, computed, inject, signal, OnInit } from '@angular/core';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { AbstractControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { FormlyErrorComponent } from '../formly-error.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { LinkButtonComponent } from '../link-button.component';
import { catchError, of, Observable, Subject } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { debounceTime, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { CompaniesService } from '../../../api/services/companies.service';
import type { CompanySearchResult } from '../../../api/models/company-search-result';
import { CompanySearchFieldComponent } from './company-search-field.component';
import { ApiMappingsStore } from '../../../features/admin/modules/features/api-mappings/api-mapping.store';
import { UtilityService } from '../../services/utility.service';
import { DateHelperService } from '../../services/date-helper.service';
import { EntryPatchService } from './services/entry-patch.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { FuseAlertComponent } from '../../../../@fuse/components/alert';
import {
  getKvkSubFieldDefs,
  KvkFieldConfig,
  KvkFieldType,
  KvkValue,
  readKvkSubValue,
  ResolvedKvkField,
  writeKvkSubValue,
} from './kvk.types';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { ClearableFieldType } from './clearable-field.type';

/** One configured KVK sub-field, with its current value pulled from the object model. */
interface KvkItem extends ResolvedKvkField {
  value: unknown;
  /** Columns this input takes in the 6-column manual-entry grid. See {@link KVK_INPUT_SPANS}. */
  span?: 2 | 3 | 6;
}

/**
 * Width of each manual-entry input, in columns of a 6-column grid: 2 = a third, 3 = a half, 6 = full.
 *
 * Mirrors `ADDRESS_FIELDS` in fieldGroup.config.ts, so a KVK address is filled in with the same
 * rhythm as a plain address field: house number and its suffix are short and sit next to the street,
 * postcode and city pair up, country gets its own half. Anything not listed (the company name aside)
 * is an author-configured extra and falls back to a half.
 */
const KVK_INPUT_SPANS: Readonly<Record<string, 2 | 3 | 6>> = {
  companyName: 6,
  'address.streetName': 2,
  'address.number': 2,
  'address.numberSuffix': 2,
  'address.postalCode': 3,
  'address.city': 3,
  'address.country': 3,
};

/** The address sub-keys a KVK lookup can hand to an `address` field, in that field's own shape. */
const KVK_ADDRESS_SUB_KEYS = [
  'streetName',
  'number',
  'numberSuffix',
  'postalCode',
  'city',
  'country',
  'fullAddress',
] as const;

/**
 * Shortest query that is searched for. Mirrors the backend's own minimum
 * (`KvkCompanySearchService.MinimumNameLength`): below it the register answers nothing, so asking is
 * a round trip that can only come back empty.
 */
const COMPANY_SEARCH_MIN_LENGTH = 3;

/** How many suggestions the field offers. More than this is a list to scroll, not one to pick from. */
const COMPANY_SEARCH_MAX_RESULTS = 10;

/** Debounce on typing: long enough that a word is finished, short enough to feel like it answers. */
const COMPANY_SEARCH_DEBOUNCE_MS = 350;

/** What one company search came back with, tagged with the query it answers. */
interface CompanySearchOutcome {
  token: number;
  /** The suggestions, or null when the query was too short to ask the register about. */
  results: CompanySearchResult[] | null;
  /** True when the search itself failed — which is not the same as the company not existing. */
  failed: boolean;
}

@Component({
  selector: 'formly-field-kvk',
  template: `
    @if (verifyStyle) {
      <!-- Verify style: value is one object on the field key ({ kvkNumber, manual, lookedUp,
           <subKey>... }). Number lookup fills the configured sub-fields inside this object. -->
      <div class="flex w-full flex-col">
        <!--
          Author-configured notice above the field (Figma: the eHerkenning prefill message).
          Dismissible, because it is informational only and the form below stays usable without it.
        -->
        @if (notice && !noticeDismissed()) {
          <!--
            The brand primary is the same deep indigo in both schemes, so a 6% tint plus an indigo
            icon disappears on the dark surface. Dark gets a stronger fill and a white icon.
          -->
          <div
            class="mb-4 flex items-start gap-3 rounded-lg border-l-4 border-primary bg-primary/[0.06] px-4 py-3 dark:bg-primary/25"
            role="status"
            data-cy="kvk-notice">
            <mat-icon
              class="icon-size-5 mt-0.5 flex-none text-primary dark:text-white"
              svgIcon="heroicons_solid:information-circle" />
            <p class="m-0 flex-1 text-sm leading-snug">{{ notice }}</p>
            <button
              type="button"
              class="-mr-1 -mt-1 flex-none border-0 bg-transparent p-0"
              aria-label="Melding sluiten"
              (click)="noticeDismissed.set(true)">
              <mat-icon class="icon-size-5">close</mat-icon>
            </button>
          </div>
        }
        <!--
          No field.className here: formly already applies it to the host <formly-field>, so
          repeating it compounded width classes (a half-width field at 50% of 50%).
        -->
        <!--
          Search mode adds a way to FIND the company; it does not replace the field's value. The
          number below stays the value that is stored and saved, so it remains visible and editable
          — a box showing a company name over a hidden number is a field that cannot be checked.
          Read-only has nothing to search for, so the bar goes rather than sitting there dead.
        -->
        @if (searchMode && !readOnly()) {
          <app-company-search-field
            class="w-full"
            label="Bedrijf zoeken"
            [placeholder]="props.placeholder || 'Zoek op bedrijfsnaam of KVK-nummer'"
            [disabled]="isDisabled()"
            [loading]="isSearching()"
            [suggestions]="suggestions()"
            [value]="searchBoxText()"
            [notFound]="searchNotFound()"
            [failed]="searchFailed()"
            [minQueryLength]="minQueryLength"
            (queryChange)="onSearchQuery($event)"
            (suggestionPicked)="onCompanyPicked($event)"
            (searchBlur)="onSearchBlur()">
            <!-- searchOnly hides the number field and its clear button, so this one clears the
                 picked company too — otherwise the only way to empty the field is a detour
                 through manual entry. -->
            @if (searchOnly) {
              <app-clear-field-button
                companySearchSuffix
                [field]="field"
                [hasValue]="searchBoxText() !== '' || hasClearableValue()"
                [disabled]="isDisabled()"
                ariaLabel="Bedrijf wissen"
                (clearSelection)="clear()" />
            } @else {
              <app-clear-field-button
                companySearchSuffix
                [field]="field"
                [hasValue]="searchBoxText() !== ''"
                [disabled]="isDisabled()"
                ariaLabel="Zoekopdracht wissen"
                (clearSelection)="clearSearchBox()" />
            }
          </app-company-search-field>
          <!-- The number field's own message is hidden with it in searchOnly; a picked suggestion
               without a valid number would otherwise do nothing, silently. -->
          @if (searchOnly && !manual() && invalidNumber()) {
            <div class="mt-1 text-sm text-warn" data-cy="kvk-invalid-pick">Kies een bedrijf met een geldig KVK-nummer.</div>
          }
        }
        @if (!searchOnly || manual() || readOnly()) {
        <div class="pb-2">
          <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
            <mat-form-field
              class="w-full sm:flex-1 min-w-0"
              [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
              <mat-label>
                <app-field-label-row [label]="props.label" [required]="props.required" [info]="props.info"></app-field-label-row>
              </mat-label>
              <!-- Not bound to the formControl (the value is one object on the field key), so the
                   read-only state has to be applied explicitly — formly's own disable never
                   reaches this input. -->
              <input
                #verifyKvkInput
                type="text"
                matInput
                [id]="id"
                [required]="props.required"
                [disabled]="isDisabled() || readOnly()"
                [placeholder]="props.placeholder ? props.placeholder : '12345678'"
                [value]="kvkNumber()"
                (input)="setNumber($any($event.target).value)"
                (keydown.enter)="$event.preventDefault(); lookup()" />
              <span matSuffix class="dd-clear-suffix dd-addon-suffix">
                @if (!isLoading() && confirmed()) {
                  <mat-icon class="dd-addon-suffix__passive text-primary icon-size-5">check_circle</mat-icon>
                }
                <app-clear-field-button
                  [field]="field"
                  [hasValue]="hasClearableValue()"
                  [disabled]="isDisabled()"
                  [ariaLabel]="clearAriaLabel()"
                  [focusTarget]="verifyKvkInput"
                  (clearSelection)="clear()" />
              </span>
              @if (props.description) {
                <mat-hint>{{ props.description }}</mat-hint>
              }
            </mat-form-field>
            <!-- Read-only shows values only: a lookup can never run there, so the button goes
                 rather than sitting dead next to the field. In search mode picking a suggestion
                 runs the lookup for you, but the button stays: a number typed straight into the
                 field, and a retry after a failed lookup, both still need it. -->
            @if (!readOnly()) {
              <button
                mat-stroked-button
                type="button"
                class="dd-kvk-lookup shrink-0 self-start"
                [disabled]="isLoading() || !isValidNumber() || formControl.disabled || props.disabled"
                (click)="lookup()">
                @if (isLoading()) {
                  <mat-spinner diameter="18"></mat-spinner>
                } @else {
                  Opzoeken
                }
              </button>
            }
          </div>
          @if (invalidNumber()) {
            <div class="mt-1 text-sm text-warn">Een KVK-nummer bestaat uit 8 cijfers.</div>
          }
        </div>
        }

        <!-- Read-only always renders the sub-fields, so there is nothing left to toggle. -->
        @if (!readOnly()) {
          <div class="mb-3">
            <!-- Figma gives this link Label/Link/Disabled, a step quieter than the lookup button
                 next to it: it is the fallback, not the expected action. -->
            <button
              type="button"
              dd-link-button
              tone="muted"
              icon="heroicons_outline:pencil-square"
              iconClass="icon-size-5"
              (click)="toggleManual()">
              {{ manual() ? 'Handmatige invoer verbergen' : 'Bedrijfsgegevens handmatig invullen' }}
            </button>
          </div>
        }

        <!--
          The read-only rows are driven by the configured sub-fields, but the confirmation line is
          not: a mapping that writes straight to sibling form fields configures no sub-fields at
          all, and the user must still see that the company came from the KvK register.
        -->
        @if (editableItems().length || confirmed()) {
          @if ((manual() || readOnly()) && editableItems().length) {
            <!-- Manual entry: the configured sub-fields become editable, prefilled with whatever a
                 lookup already returned (same object keys). A read-only context always lands here
                 (never on the summary card) and renders the same inputs, disabled. -->
            <div class="mb-4 grid w-full grid-cols-1 gap-3 sm:grid-cols-6">
              @for (item of editableItems(); track item.subKey) {
                <!-- Widths come from the item itself (KVK_INPUT_SPANS), so the address parts line up
                     the way the address field does instead of every input taking a stubborn half. -->
                <mat-form-field
                  class="w-full"
                  [class.sm:col-span-2]="item.span === 2"
                  [class.sm:col-span-3]="item.span === 3"
                  [class.sm:col-span-6]="item.span === 6">
                  <mat-label>{{ item.label }}</mat-label>
                  <input
                    matInput
                    [type]="item.type"
                    [disabled]="readOnly()"
                    [value]="item.value ?? ''"
                    (input)="setField(item.subKey, $any($event.target).value, item.type)" />
                </mat-form-field>
              }
            </div>
          } @else if (confirmed()) {
            <!--
              Confirmed lookup (Figma): the retrieved company details sit in their own tinted
              summary card, with the source confirmation directly underneath it.
            -->
            <div class="mb-4 flex flex-col gap-2">
              @if (readOnlyItems().length || searchOnly) {
                <!--
                  Figma "surface/custom" (#f6faff): a blue-tinted panel, not the neutral grey the
                  rest of the form uses. Dark mode keeps the same hue at low opacity so the tint
                  still reads as "retrieved data" instead of turning into another grey box.
                -->
                <div
                  class="flex flex-col gap-1 rounded-lg bg-[#f6faff] px-5 py-4 dark:bg-[#2b3446]"
                  data-cy="kvk-summary">
                  <!-- searchOnly has no number input, so the card is the only place the number shows
                       (read-only renders the input again, so the row would repeat it). -->
                  @if (searchOnly && !readOnly()) {
                    <div class="flex flex-col gap-0.5 text-sm leading-[1.4] sm:flex-row sm:items-baseline sm:gap-2">
                      <span class="flex-none text-accent sm:w-40">KVK-nummer</span>
                      <span class="font-medium" data-cy="kvk-summary-number">{{ kvkNumber() }}</span>
                    </div>
                  }
                  @for (item of readOnlyItems(); track item.subKey) {
                    <div class="flex flex-col gap-0.5 text-sm leading-[1.4] sm:flex-row sm:items-baseline sm:gap-2">
                      <span class="flex-none text-accent sm:w-40">{{ item.label }}</span>
                      <span class="font-medium">{{ item.display }}</span>
                    </div>
                  }
                </div>
              }
              <div class="flex items-center gap-2 text-xs text-accent">
                <span class="inline-block h-2 w-2 flex-none rounded-full bg-blue-500"></span>
                Bevestigd uit het KvK-register
              </div>
            </div>
          }
        }

        @if (lookupError(); as error) {
          <fuse-alert class="pb-6" type="error">{{ error }}</fuse-alert>
        }
        <!-- Deregistered-at-the-KvK warning, same signal the classic style renders. -->
        @if (showWarning()) {
          <fuse-alert class="pb-6" [type]="warningType()">{{ warningMessage() }}</fuse-alert>
        }
        @if (props.required && formControl.touched && formControl.hasError('kvkRequired')) {
          <div class="mt-1 text-sm text-warn">
            {{
              manual()
                ? 'Voer een geldig KVK-nummer in.'
                : searchOnly
                  ? 'Kies een bedrijf.'
                  : 'Voer een geldig KVK-nummer in en haal de bedrijfsgegevens op.'
            }}
          </div>
        }
        <!-- kvkRequired has its own message above; the generic error would print its raw value. -->
        @if (!formControl.hasError('kvkRequired')) {
          <app-formly-error [field]="field"></app-formly-error>
        }
      </div>
    } @else {
      <div>
        <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3 w-full">
          <mat-form-field
            class="w-full sm:flex-1 min-w-0"
            [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
            <!-- No required sign needed here it's in the label-->
            <mat-label>
              <app-field-label-row [label]="props.label" [required]="props.required" [info]="props.info"></app-field-label-row>
            </mat-label>
            <input
              #classicKvkInput
              type="text"
              matInput
              [required]="props.required"
              [placeholder]="this.field.props.placeholder ? this.field.props.placeholder : '12345678'"
              [formControl]="formControl"
              [formlyAttributes]="field"
              (blur)="onBlur()"
              (keydown.enter)="onEnter($event)" />
            <span matSuffix class="dd-clear-suffix dd-addon-suffix">
              @if (!isLoading() && lookupSucceeded()) {
                <mat-icon class="dd-addon-suffix__passive text-primary icon-size-5">check_circle</mat-icon>
              }
              <app-clear-field-button
                [field]="field"
                [hasValue]="hasClearableValue()"
                [disabled]="isDisabled()"
                [ariaLabel]="clearAriaLabel()"
                [focusTarget]="classicKvkInput"
                (clearSelection)="clear()" />
            </span>
            @if (props.description) {
              <mat-hint>{{ props.description }}</mat-hint>
            }
          </mat-form-field>
          <button
            mat-stroked-button
            type="button"
            class="dd-verify-button shrink-0 self-start"
            [disabled]="isLoading() || !isValidKvkNumber() || formControl.disabled || props.disabled"
            (click)="verify()">
            <span class="dd-verify-content">
              @if (isLoading()) {
                <mat-spinner class="dd-verify-spinner" diameter="18"></mat-spinner>
              }
              <span class="dd-verify-label">Gegevens ophalen</span>
            </span>
          </button>
        </div>
        @if (showWarning()) {
          <fuse-alert class="pb-6" [type]="warningType()">{{ warningMessage() }}</fuse-alert>
        }
        <app-formly-error [field]="field"></app-formly-error>
      </div>
    }
  `,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatInputModule,
    MatButtonModule,
    FormlyErrorComponent,
    LinkButtonComponent,
    MatProgressSpinner,
    MatIcon,
    FuseAlertComponent,
    ClearFieldButtonComponent,
    CompanySearchFieldComponent,
    FieldLabelRowComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      /* Fixed width so the button keeps the same size regardless of label length
         or loading state. Both verify buttons (KVK/IBAN) share min-width 12rem so
         they render identically, matching the design. margin-top 2px centers the
         36px button within the ~40px field box (floating-label appearance). */
      .dd-verify-button {
        min-width: 12rem;
      }

      /* Lay the spinner inline, left of the label, on a single line. min-width
         absorbs the extra spinner width so the button keeps the same size. */
      .dd-verify-content {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        white-space: nowrap;
      }

      .dd-verify-spinner {
        flex: 0 0 auto;
      }

      /* Keep the label upright — the global '[disabled] *' rule (tailwind.scss)
         italicizes descendants of disabled elements, but the design has no
         italic. */
      .dd-verify-label {
        font-style: normal;
      }

      /* Beside-the-field layout only kicks in from the sm breakpoint (640px);
         below that the button stacks under the field (mobile design), where the
         alignment offset must not apply. margin-top 2px centers the 36px button
         within the ~40px field box. */
      @media (min-width: 640px) {
        .dd-verify-button {
          margin-top: 2px;
        }

        /* Label-placement "above" reserves --dd-label-above-reserve above the box for
           the external label (.labels-above in _forms.scss). Push the button down past
           it, plus the 2px that centres the 36px button in the ~40px box. */
        :host-context(.labels-above) .dd-verify-button {
          margin-top: calc(var(--dd-label-above-reserve, 28px) + 2px);
        }
      }

      /* Verify-style lookup button: outlined, matches the ~40px field-box height. */
      .dd-kvk-lookup {
        min-width: 8rem;
      }

      @media (min-width: 640px) {
        .dd-kvk-lookup {
          margin-top: 2px;
        }

        :host-context(.labels-above) .dd-kvk-lookup {
          margin-top: calc(var(--dd-label-above-reserve, 28px) + 2px);
        }
      }
    `,
  ],
})
export class FormlyKvkType extends ClearableFieldType implements OnInit {
  destroy$ = injectDestroy();
  apiMappingsStore = inject(ApiMappingsStore);
  private readonly entryPatchService = inject(EntryPatchService);
  readonly #companiesService = inject(CompaniesService);

  // Signals to track component state
  isValidKvkNumber = signal(false);
  isLoading = signal(false);
  showWarning = signal(false);
  warningMessage = signal('');
  warningType = signal<'error' | 'warning'>('error');
  // True only after a successful lookup — drives the "verified" check icon.
  lookupSucceeded = signal(false);
  readonly #lookupCancelled$ = new Subject<void>();

  // --- Verify style ---------------------------------------------------------
  /** True when the form builder configured the verify style for this field. */
  verifyStyle = false;
  /** Author-configured notice above the field; empty renders nothing. */
  notice = '';
  /** The verify field's value is an object: `{ kvkNumber, manual, lookedUp, <sub-fields> }`. */
  readonly #obj = signal<KvkValue>({});
  /** Message shown when the lookup request itself fails (e.g. a 500 from the mapping API). */
  readonly lookupError = signal<string | null>(null);
  /** Flagged when the user triggers a lookup with an invalid number. */
  readonly invalidNumber = signal(false);
  /** Whether the author-configured notice above the field was closed by the user. */
  readonly noticeDismissed = signal(false);

  // --- Search mode ----------------------------------------------------------
  /**
   * True when the field looks companies up by name instead of asking for a number. Verify style
   * only: that style stores an object, which is what lets the box show a company name while the
   * value keeps the number.
   */
  searchMode = false;
  /**
   * `kvkLookupMode: 'searchOnly'` — search mode without the number input and the Opzoeken button:
   * the number can only come from a picked suggestion, and shows on the summary card instead.
   * Manual entry and the read-only view still render the input, since there the number is typed
   * or merely displayed.
   */
  searchOnly = false;
  readonly minQueryLength = COMPANY_SEARCH_MIN_LENGTH;
  /** What the user typed, on its way to the register. */
  readonly #searchQuery$ = new Subject<string>();
  readonly suggestions = signal<CompanySearchResult[]>([]);
  readonly isSearching = signal(false);
  /** The register answered the last query with nothing. */
  readonly searchNotFound = signal(false);
  /** The last search could not be run at all (offline, 500). Distinct from "no such company". */
  readonly searchFailed = signal(false);
  /**
   * Monotonic id of the in-flight search. switchMap drops a query superseded by a newer one, but
   * picking a suggestion or clearing the field abandons a query WITHOUT emitting on the stream —
   * so those bump the token instead, and a response tagged with a stale one is discarded.
   */
  #searchToken = 0;

  /**
   * Read-only rendering: no typing, no lookup, no manual-entry toggle.
   *
   * Kept as a signal rather than read straight off `formControl` / `props` in the template,
   * because neither is reactive — it is seeded in `ngOnInit` and refreshed from the control's
   * status stream, which is what emits on enable/disable.
   */
  readonly #readOnly = signal(false);
  readonly readOnly = this.#readOnly.asReadonly();

  readonly kvkNumber = computed(() => (this.#obj()['kvkNumber'] as string) ?? '');
  readonly manual = computed(() => this.#obj()['manual'] === true);
  readonly isValidNumber = computed(() => this.validateKvkNumber(this.kvkNumber()));
  /** Confirmed = looked up via KVK and not in manual mode; drives the check + read-only view. */
  readonly confirmed = computed(() => this.#obj()['lookedUp'] === true && !this.manual());

  /**
   * What the search bar shows. Its own state on purpose: the bar is how a company is FOUND, the
   * number field below is what is stored. Tying the two together is what made the field show a
   * company name while the number it saves was nowhere on screen.
   */
  readonly #searchBox = signal('');
  readonly searchBoxText = this.#searchBox.asReadonly();

  /**
   * The sub-fields this field renders: the fixed company/address keys plus every extra configured
   * apiField. Configured apiFields that alias onto a fixed key (naam, straatnaam, postcode, …) only
   * decide where the lookup writes; they add no second input.
   */
  readonly kvkFieldDefs = computed<ResolvedKvkField[]>(() =>
    getKvkSubFieldDefs(this.props['kvkFields'] as Array<string | KvkFieldConfig> | undefined)
  );

  /** The configured sub-fields with their current values (recomputes as the object model changes). */
  readonly kvkItems = computed<KvkItem[]>(() => {
    const model = this.#obj();
    return this.kvkFieldDefs().map(def => ({ ...def, value: readKvkSubValue(model, def.subKey) }));
  });

  /**
   * The sub-fields rendered as INPUTS (manual entry, and the disabled read-only variant of it).
   *
   * `props.kvkHideCompanyName` drops the company name here and only here: on a form that already has
   * its own company-name field, typing the name twice is the thing to avoid. The retrieved value
   * stays visible on the summary card — the statutory name from the register is exactly the result
   * of the lookup, and it is what shows that the register's name differs from the trade name people
   * use. Never dropped from the value either: the lookup keeps writing `<keyPath>.companyName`, so
   * the mapping and the not-found check below keep working.
   */
  readonly editableItems = computed<KvkItem[]>(() => {
    const hideCompanyName = this.props['kvkHideCompanyName'] === true;
    return this.kvkItems()
      .filter(item => !(hideCompanyName && item.subKey === 'companyName'))
      .map(item => ({ ...item, span: KVK_INPUT_SPANS[item.subKey] ?? 3 }));
  });

  /**
   * Filled sub-fields only — used for the read-only view, with dates shown as dd-mm-yyyy.
   *
   * The address collapses into a single "Bedrijfsadres" row (Figma), while the manual inputs keep
   * one field per address part: a summary reads as an address, a form has to be editable per part.
   * `fullAddress` is preferred when the lookup returned it; otherwise the parts are composed.
   */
  readonly readOnlyItems = computed<Array<KvkItem & { display: unknown }>>(() => {
    const filled = this.kvkItems().filter(
      item => item.value !== null && item.value !== undefined && item.value !== ''
    );

    const rows: Array<KvkItem & { display: unknown }> = [];
    let addressAdded = false;

    for (const item of filled) {
      if (!item.subKey.startsWith('address.')) {
        rows.push({
          ...item,
          // On the card the name is what the register holds, which is not necessarily the name people
          // use — a company may trade under an alias that lives in its own company-name field. Saying
          // "Statutaire naam" here is what makes that difference readable; the input keeps the plain
          // "Bedrijfsnaam" label, because in manual mode there is no register to be statutory about.
          label: item.subKey === 'companyName' ? 'Statutaire naam' : item.label,
          display: item.type === 'date' ? DateHelperService.formatIsoDateDisplay(item.value) : item.value,
        });
        continue;
      }
      if (addressAdded) {
        continue;
      }
      const display = this.#composedAddress(filled);
      if (display) {
        rows.push({ ...item, subKey: 'address', label: 'Bedrijfsadres', display });
        addressAdded = true;
      }
    }

    return rows;
  });

  /**
   * One-line address: the lookup's own full address, else street/number/suffix, postcode and city.
   *
   * `fullAddress` is read from the value, not from `items`: it is an alias-only sub-key that never
   * becomes a rendered sub-field, so it is absent from the item list even when the lookup filled it.
   */
  #composedAddress(items: KvkItem[]): string {
    const value = (subKey: string): string => {
      const found = items.find(item => item.subKey === subKey)?.value;
      return typeof found === 'string' || typeof found === 'number' ? String(found).trim() : '';
    };

    const stored = readKvkSubValue(this.#obj(), 'address.fullAddress');
    const full = typeof stored === 'string' ? stored.trim() : '';
    if (full) {
      return full;
    }

    const street = [value('address.streetName'), `${value('address.number')}${value('address.numberSuffix')}`]
      .filter(Boolean)
      .join(' ')
      .trim();

    // Country only when it is not the Dutch default: the design shows "street, postcode, city" for
    // a domestic company, but dropping the country outright would silently lose the one part that
    // matters for a foreign one.
    const country = value('address.country');
    const foreign = country !== '' && country.toLowerCase() !== 'nederland';

    return [street, value('address.postalCode'), value('address.city'), foreign ? country : '']
      .filter(Boolean)
      .join(', ');
  }

  constructor() {
    super();
  }

  ngOnInit() {
    this.verifyStyle = this.props['kvkStyle'] === 'verify';
    const notice = this.props['kvkNotice'];
    this.notice = typeof notice === 'string' ? notice.trim() : '';

    this.#syncReadOnly();
    this.formControl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.#syncReadOnly());

    if (this.verifyStyle) {
      // Search mode is verify-only: the standard style stores the bare number as its value, so a box
      // showing a company name there would no longer be the value the form saves.
      this.searchOnly = this.props['kvkLookupMode'] === 'searchOnly';
      this.searchMode = this.searchOnly || this.props['kvkLookupMode'] === 'search';
      this.#initVerify();
      if (this.searchMode) {
        this.#initCompanySearch();
      }
      return;
    }

    // Live format validation only — drives the button's enabled state and the
    // "valid" indicator. The actual KVK lookup is triggered by the button.
    this.field.formControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.evaluateFormat(value));

    this.evaluateFormat(this.field.formControl.value);
  }

  onEnter(event: Event) {
    event.preventDefault();
    if (!this.isLoading() && this.isValidKvkNumber()) {
      this.verify();
    }
  }

  /** Deviates: cancels a running lookup and resets the composite object value plus the lookup state. */
  override clear() {
    if (this.formControl.disabled) {
      return;
    }
    this.#lookupCancelled$.next();
    this.isLoading.set(false);
    if (this.verifyStyle) {
      this.#obj.set({});
      this.formControl.setValue({});
    } else {
      this.formControl.setValue('');
    }
    this.lookupSucceeded.set(false);
    this.showWarning.set(false);
    this.warningMessage.set('');
    this.lookupError.set(null);
    this.invalidNumber.set(false);
    this.isValidKvkNumber.set(false);
    this.#searchBox.set('');
    this.#abandonSearch();
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }

  /** Deviates: in verify style the value is an object, so emptiness is the KVK number, not the value. */
  override hasClearableValue(): boolean {
    if (this.verifyStyle) {
      return this.kvkNumber() !== '';
    }
    const value = this.formControl.value;
    return value !== null && value !== undefined && value !== '';
  }

  onBlur() {
    // An incomplete number (too few digits) is only flagged once the user leaves
    // the field, to avoid nagging mid-typing. Don't override a live format error.
    const value = this.formControl.value;
    if (value && !this.isValidKvkNumber() && !this.showWarning()) {
      this.warningType.set('error');
      this.warningMessage.set('Een KVK-nummer bestaat uit 8 cijfers.');
      this.showWarning.set(true);
    }
  }

  verify() {
    // Read-only contexts (admin review page, view-only roles) must never trigger
    // a lookup that patches the form.
    if (this.formControl.disabled || this.props.disabled) {
      return;
    }

    const value = this.field.formControl.value;
    if (!value) {
      return;
    }

    // Check if the KVK number contains spaces
    if (value.includes(' ')) {
      this.warningType.set('error');
      this.warningMessage.set('Voer het KVK-nummer in zonder spaties.');
      this.showWarning.set(true);
      return;
    }

    // Validate the KVK number format
    if (!this.validateKvkNumber(value)) {
      this.warningType.set('error');
      this.warningMessage.set('Ongeldig KVK-nummer. Een KVK-nummer bestaat uit 8 cijfers.');
      this.showWarning.set(true);
      return;
    }

    this.#lookupCancelled$.next();
    this.isLoading.set(true);
    this.showWarning.set(false);

    let entry = this.options.formState.entry;
    entry = UtilityService.setKeyValuePath(entry, this.field.props.keyPath, value);

    // Request company information from the API
    this.apiMappingsStore
      .applyMapping({
        mappingId: this.field.props.apiMappingId,
        moduleId: this.options.formState.apiMappingModuleId ?? this.options.formState.entity.id,
        entry: entry,
        message: false,
      })
      .pipe(
        takeUntil(this.#lookupCancelled$),
        catchError(error => {
          console.error(error);
          this.warningType.set('error');
          if (error['status'] === 404) {
            this.warningMessage.set(
              'Fout bij het ophalen van KVK gegevens. Configuratie van de API mapping is mogelijk onjuist.'
            );
          } else {
            this.warningMessage.set('KVK-nummer kon niet worden geverifieerd.');
          }
          this.lookupSucceeded.set(false);
          this.showWarning.set(true);
          this.isLoading.set(false);
          return of();
        })
      )
      .subscribe(entry => {
        if (!entry) {
          this.isLoading.set(false);
          return;
        }
        // Update the form with the retrieved company information. Uses EntryPatchService
        // (not a plain patchValue) because the KvK result can land on a page other than the
        // one currently active — without marking it dirty, DetailsService.performSave's
        // page-only save would silently drop it (#20789).
        const rootField = UtilityService.getFormOfRootForm(this.field);
        this.entryPatchService.applyEntryPatchToRootForm(rootField, entry.entry);

        // Lookup succeeded → show the verified check on the input.
        this.lookupSucceeded.set(true);

        // KVK uitgeschreven? — api-mapping write 'materieleRegistratie.datumEinde'
        // naar een veld waarvan de key 'kvk-eind-datum' of 'kvk-end-date' bevat.
        // Pad varieert per mapping; recursief zoeken is robuust voor toekomstige mappings.
        const endDate = this.findKvkEndDate(entry.entry);
        if (endDate) {
          this.warningType.set('warning');
          this.warningMessage.set(
            `KVK-nummer is uitgeschreven bij de Kamer van Koophandel (einddatum: ${this.formatDate(endDate)}). Controleer of dit bedrijf nog actief is.`
          );
          this.showWarning.set(true);
        }

        this.isLoading.set(false);
      });
  }

  // --- Verify style methods -------------------------------------------------

  /** Set up the object model, required validator, and external-patch sync (verify style only). */
  #initVerify() {
    this.#obj.set(this.#normalize(this.formControl.value));

    this.formControl.addValidators(this.#kvkRequiredValidator);
    this.formControl.updateValueAndValidity({ emitEvent: false });

    // Keep the local object mirror in sync with external patches (defaults, resets, etc.).
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.#obj.set(this.#normalize(value)));
  }

  #syncReadOnly() {
    this.#readOnly.set(this.formControl.disabled || this.props.disabled === true);
  }

  // --- Search mode methods --------------------------------------------------

  /**
   * Runs the company search: what is typed is debounced into one query, and only the answer to the
   * most recent one is shown (switchMap drops the rest).
   */
  #initCompanySearch() {
    this.#searchQuery$
      .pipe(
        // Deliberately no distinctUntilChanged: it has no way of being reset, so once a query has
        // been searched for, asking for the same text again — search a company, clear the field,
        // search for it again — is dropped as a duplicate and the field answers nothing. The
        // debounce already collapses a burst of typing, and switchMap drops a superseded request,
        // so the only thing it would still save is a repeat the user explicitly asked for.
        debounceTime(COMPANY_SEARCH_DEBOUNCE_MS),
        switchMap((query): Observable<CompanySearchOutcome> => {
          const token = ++this.#searchToken;
          const trimmed = query.trim();

          // Too short to search for: the register matches whole words, so a first word still being
          // typed can only come back empty. Say nothing rather than "not found".
          if (trimmed.length < COMPANY_SEARCH_MIN_LENGTH) {
            this.suggestions.set([]);
            this.searchNotFound.set(false);
            this.searchFailed.set(false);
            this.isSearching.set(false);
            return of({ token, results: null, failed: false });
          }

          this.isSearching.set(true);
          return this.#companiesService
            .searchCompanies({ body: { name: trimmed, maxResults: COMPANY_SEARCH_MAX_RESULTS } })
            .pipe(
              // Array-guarded: a body that is not a list (a proxy error page, a changed contract)
              // would otherwise reach the template and throw while rendering the option rows.
              map(results => ({ token, results: Array.isArray(results) ? results : [], failed: false })),
              catchError((error: unknown) => {
                console.error('Company search failed', error);
                return of({ token, results: [] as CompanySearchResult[], failed: true });
              }),
              finalize(() => {
                // Only the current search owns the spinner; an abandoned one must not switch it off
                // under the search that replaced it.
                if (token === this.#searchToken) {
                  this.isSearching.set(false);
                }
              })
            );
        }),
        // Last in the pipe: before switchMap it only completes the source, which leaves an
        // already-running request to finish and write its result into a destroyed component.
        takeUntil(this.destroy$)
      )
      .subscribe(({ token, results, failed }) => {
        // A pick, a clear or a newer query bumped the token after this request started, so what it
        // answers is no longer what the field is asking. `null` = the query was too short to run.
        if (token !== this.#searchToken || results === null) {
          return;
        }
        this.suggestions.set(results);
        this.searchFailed.set(failed);
        this.searchNotFound.set(!failed && results.length === 0);
      });
  }

  /** Abandons whatever search is in flight, so its answer can no longer land on the field. */
  #abandonSearch() {
    this.#searchToken++;
    this.isSearching.set(false);
    this.suggestions.set([]);
    this.searchNotFound.set(false);
    this.searchFailed.set(false);
  }

  /** A keystroke in the search box. */
  onSearchQuery(query: string) {
    if (this.readOnly()) {
      return;
    }

    // Typing deliberately does NOT touch the stored value. What is typed is a query; the number
    // below only changes when a suggestion is picked or it is typed in directly.
    this.#searchBox.set(query);

    // A lookup started for the company that was in the box is answering a question the user has
    // moved on from; letting it finish would patch the form with that company while the box shows
    // the new query.
    this.#lookupCancelled$.next();
    this.isLoading.set(false);

    this.lookupError.set(null);
    this.#searchQuery$.next(query);
  }

  /**
   * A suggestion was picked: its number becomes the field's value and the configured lookup runs
   * straight away, so the company details fill in without a second action.
   */
  onCompanyPicked(company: CompanySearchResult) {
    if (this.readOnly()) {
      return;
    }

    const number = (company.chamberOfCommerceNumber ?? '').replace(/\s/g, '');

    // A suggestion the register cannot identify is not a company this field can hold: storing its
    // name against no number leaves a value that never looks up and that `hasClearableValue` (which
    // reads the number) refuses to offer a clear button for.
    if (!this.validateKvkNumber(number)) {
      this.#abandonSearch();
      this.invalidNumber.set(true);
      return;
    }

    this.#abandonSearch();
    this.invalidNumber.set(false);
    this.lookupError.set(null);
    this.showWarning.set(false);

    // Number first: it is what drops the previous company's details. Writing the name before it
    // would put the new name in the object the number change then clears.
    this.#patch({ kvkNumber: number, lookedUp: false, manual: false, checkPending: false });

    // The name is stored before the lookup answers, so the summary reads as the picked company
    // right away — and keeps reading as it even if the lookup fails.
    if (company.name) {
      this.#patch({ companyName: company.name });
      this.#searchBox.set(company.name);
    }

    this.lookup();
  }

  /**
   * The search bar lost focus. The query stays on screen — it is a search box, not the value — but
   * a request still on its way is abandoned so its answer cannot reopen the list later.
   */
  onSearchBlur() {
    this.#abandonSearch();
  }

  /** Empties the search bar only. The company that was found stays in the field below. */
  clearSearchBox() {
    this.#searchBox.set('');
    this.#abandonSearch();
  }

  setNumber(value: string) {
    if (this.readOnly()) {
      return;
    }
    const kvkNumber = (value ?? '').replace(/\s/g, '');
    const numberChanged = kvkNumber !== this.kvkNumber();
    // Editing invalidates any previous lookup. Numbers are stored bare (no spaces).
    this.#patch({ kvkNumber, lookedUp: false, checkPending: false });
    if (numberChanged) {
      this.#invalidateBagSnapshot();
    }
    this.lookupError.set(null);
    this.invalidNumber.set(false);
  }

  #invalidateBagSnapshot(): void {
    const rootField = UtilityService.getFormOfRootForm(this.field);
    const metaData = rootField?.formControl?.get('meta-data');
    if (!rootField || !metaData) {
      return;
    }

    const clearedSnapshot = {
      'bag-is-residential': null,
      'bag-matched-address': null,
      'bag-gebruiksdoel': null,
      'bag-oppervlakte': null,
      'bag-adresseerbaar-object-status': null,
      'bag-last-check-date': null,
    };
    if (!Object.keys(clearedSnapshot).some(key => metaData.get(key)?.value != null)) {
      return;
    }

    this.entryPatchService.applyEntryPatchToRootForm(rootField, { 'meta-data': clearedSnapshot });
    this.field.options?.formState?.detailsService?.triggerAsyncValidation([
      { keyPath: 'entry.meta-data.bag-is-residential', value: null },
    ]);
  }

  toggleManual() {
    if (this.readOnly()) {
      return;
    }
    this.#patch({ manual: !this.manual() });
  }

  setField(subKey: string, raw: string, type: KvkFieldType) {
    if (this.readOnly()) {
      return;
    }
    const value = type === 'number' ? (raw === '' ? null : Number(raw)) : raw;
    // Sub-keys may be dotted (`address.city`), so write through the nested writer rather than
    // setting a literal "address.city" key on the object.
    const next = writeKvkSubValue(this.#obj(), subKey, value);
    this.#obj.set(next);
    this.formControl.setValue(next);
  }

  lookup() {
    if (this.isLoading() || this.formControl.disabled || this.props.disabled) {
      return;
    }
    const number = this.kvkNumber();
    if (!this.validateKvkNumber(number)) {
      this.invalidNumber.set(true);
      return;
    }
    this.invalidNumber.set(false);
    this.#runVerifyLookup();
  }

  /**
   * Merge a partial into the object value and push it to the form control.
   *
   * Marks the control dirty: `setValue` alone does not, and every caller here is a user action
   * (typing the number, toggling manual entry, applying a lookup they asked for). Without it a
   * lookup whose mapping writes ONLY into this field's own object leaves the form pristine, and a
   * save button gated on `pristine` would refuse to save the result the user just fetched.
   */
  #patch(partial: Partial<KvkValue>) {
    const next = this.#dropCompanyDataOfPreviousNumber({ ...this.#obj(), ...partial });
    this.#obj.set(next);
    this.formControl.setValue(next);
    this.formControl.markAsDirty();
  }

  /**
   * Drops the company details as soon as the KvK number changes.
   *
   * A lookup only writes the sub-fields its mapping actually returned and leaves the rest alone.
   * Without this, moving to another company — picking a second suggestion, or retyping the number —
   * keeps whatever the previous one filled in, and the field ends up holding the new company's name
   * and number against the old company's address. Nothing on screen shows that, which is what makes
   * it worth clearing rather than merging.
   *
   * Manual entry is the user's own typing, not a lookup result, so it is never dropped — except in
   * searchOnly over a company picked from the register: there the number is the register's, and
   * retyping it by hand would save company A's details under company B's number.
   */
  #dropCompanyDataOfPreviousNumber(next: KvkValue): KvkValue {
    const previous = this.#obj();
    if (next.kvkNumber === previous.kvkNumber) {
      return next;
    }
    if (next['manual'] === true && !(this.searchOnly && previous.lookedUp === true)) {
      return next;
    }

    let cleared = next;
    for (const def of this.kvkFieldDefs()) {
      cleared = writeKvkSubValue(cleared, def.subKey, undefined);
    }
    // Alias-only: a lookup can fill it, but it is never a rendered sub-field, so it is absent from
    // the field defs above and would otherwise survive as the one line of the old company's address.
    return writeKvkSubValue(cleared, 'address.fullAddress', undefined);
  }

  /**
   * Deep copy with every empty leaf ('' / null / undefined) and every object that ends up empty
   * removed. Used to build the api-mapping request so output targets that the user has not filled
   * in are absent rather than empty — the only shape the backend will write into. Arrays are kept
   * as-is (an index-shifted array would change meaning).
   */
  #withoutEmptyLeaves(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value;
    }
    if (value === null || typeof value !== 'object') {
      return value === '' ? undefined : value;
    }
    const out: Record<string, unknown> = {};
    for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
      const pruned = this.#withoutEmptyLeaves(raw);
      if (pruned === undefined || pruned === null) {
        continue;
      }
      if (typeof pruned === 'object' && !Array.isArray(pruned) && Object.keys(pruned).length === 0) {
        continue;
      }
      out[key] = pruned;
    }
    return out;
  }

  /**
   * A copy of the mapping result without this field's own branch, so patching it into the root form
   * cannot overwrite the composite value (which is merged sub-key by sub-key instead). Empty
   * containers left behind by the removal are pruned so the patch does not blank out siblings.
   */
  #withoutOwnBranch(model: Record<string, unknown>): Record<string, unknown> {
    const segments = (this.field.props.keyPath ?? '').replace(/^entry\./, '').split('.').filter(Boolean);
    if (!segments.length) {
      return model;
    }

    const strip = (source: unknown, depth: number): unknown => {
      if (source === null || typeof source !== 'object' || Array.isArray(source)) {
        return source;
      }
      const segment = segments[depth];
      const out: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
        if (key !== segment) {
          out[key] = value;
          continue;
        }
        // Last segment = this field itself: drop it. Otherwise recurse and keep the branch only if
        // something is left in it.
        if (depth === segments.length - 1) {
          continue;
        }
        const pruned = strip(value, depth + 1);
        if (pruned && typeof pruned === 'object' && Object.keys(pruned).length) {
          out[key] = pruned;
        }
      }
      return out;
    };

    return strip(model, 0) as Record<string, unknown>;
  }

  /** Coerce whatever the control holds (object, legacy number string, or null) into the object shape. */
  #normalize(value: unknown): KvkValue {
    if (value && typeof value === 'object') {
      return value as KvkValue;
    }
    if (typeof value === 'string') {
      return value ? { kvkNumber: value } : {};
    }
    return {};
  }

  #runVerifyLookup() {
    this.#lookupCancelled$.next();
    this.isLoading.set(true);
    this.lookupError.set(null);

    // Place the current object at its keyPath so the mapping reads the number from
    // `<keyPath>.kvkNumber` and writes each result to `<keyPath>.<subKey>`.
    //
    // Empty leaves are pruned first: the backend only fills an output target whose key is ABSENT
    // from the posted entry — a key that is present but empty ('' or null) is left untouched. Form
    // controls always serialise as '' , so without this the mapping could never fill an untouched
    // field (company name, hidden kvk-exists/kvk-eind-datum). Non-empty values are kept so
    // mappings that read other entry fields as input keep working.
    const entry = UtilityService.setKeyValuePath(
      this.#withoutEmptyLeaves(this.options.formState.entry) as typeof this.options.formState.entry,
      this.field.props.keyPath,
      this.#obj()
    );

    this.apiMappingsStore
      .applyMapping({
        mappingId: this.field.props.apiMappingId,
        moduleId: this.options.formState.apiMappingModuleId ?? this.options.formState.entity.id,
        entry,
        message: false,
      })
      .pipe(
        takeUntil(this.#lookupCancelled$),
        catchError(error => {
          console.error(error);
          this.isLoading.set(false);
          this.lookupError.set(
            'Bedrijfsgegevens ophalen mislukt. Probeer het opnieuw of vul de gegevens handmatig in.'
          );
          return of();
        })
      )
      .subscribe(result => {
        if (!result) {
          this.isLoading.set(false);
          return;
        }
        const model = (result.entry as Record<string, unknown>) ?? {};

        // Apply the mapping result to the root form first, MINUS this field's own branch. A
        // verify-style KVK mapping may also write to sibling fields (hidden kvk-exists /
        // kvk-eind-datum, or a company name field elsewhere on the form); reading back only our own
        // sub-keys would silently drop those. Our own branch is excluded because a blunt patch of
        // the whole object would replace the field value wholesale — wiping the number the user
        // typed and any sub-value the mapping did not return. It is merged key by key below.
        // EntryPatchService (not a plain patchValue) so a result landing on another page still
        // marks that page dirty (#20789).
        const rootField = UtilityService.getFormOfRootForm(this.field);
        this.entryPatchService.applyEntryPatchToRootForm(rootField, this.#withoutOwnBranch(model));

        // Sub-keys may be dotted (`address.city`), so merge value by value through the nested
        // writer. A sub-field the mapping did not return keeps whatever was already filled in.
        let next = this.#obj();
        for (const def of this.kvkFieldDefs()) {
          const raw = this.#getValueAtPath(model, `${this.field.props.keyPath}.${def.subKey}`);
          if (raw === undefined) {
            continue;
          }
          next = writeKvkSubValue(next, def.subKey, def.type === 'date' ? DateHelperService.toIsoDate(raw) : raw);
        }
        // The chamber of commerce does not know this number (struck off, or a typo). The backend
        // answers that with a result instead of an error, so recognise it here: marking the field
        // as looked up would present an empty company as verified.
        //
        // "Nothing came back" is the test, not "no company name": a mapping is free to write only
        // an address, or to put the name on a sibling field instead of inside this object, and
        // neither of those means the number is unknown.
        const lookupFilledSomething = this.kvkFieldDefs().some(def => {
          const value = readKvkSubValue(next, def.subKey);
          return value !== undefined && value !== null && value !== '';
        });
        if (!lookupFilledSomething) {
          this.isLoading.set(false);
          this.lookupError.set(
            'Dit KVK-nummer is niet bekend bij de Kamer van Koophandel. Controleer het nummer of vul de gegevens handmatig in.'
          );
          return;
        }

        // checkPending: a successful lookup is a KvK check. A backend action on the module stamps
        // the check dates once the entry is saved and resets the flag (same contract as the RDW field).
        this.#patch({ ...next, lookedUp: true, manual: false, checkPending: true });
        this.#prefillAddressTarget(rootField, next);

        // KVK uitgeschreven? Same signal as the classic style: any key like `kvk-eind-datum`
        // anywhere in the result (typically a hidden field fed by the mapping).
        const endDate = this.findKvkEndDate(model);
        if (endDate) {
          this.warningType.set('warning');
          this.warningMessage.set(
            `KVK-nummer is uitgeschreven bij de Kamer van Koophandel (einddatum: ${this.formatDate(endDate)}). Controleer of dit bedrijf nog actief is.`
          );
          this.showWarning.set(true);
        }

        this.isLoading.set(false);
      });
  }

  /**
   * Copies the looked-up KvK address into the form's registered-address field (`props.kvkAddressTarget`,
   * a keyPath such as `general.address`) — but ONLY while that field is still empty.
   *
   * "Only when empty" is the same rule the server-side action "KvK adres: overnemen als bedrijfsadres
   * nog leeg is" applies on entryUpdated; doing it here as well means a new company sees its address
   * immediately instead of after the first save. Overwriting a filled address is deliberately not an
   * option: the KvK register carries the registration address (house number 14) while a company may
   * sit at 14C, and the api-mapping used to overwrite that correction on every periodic check.
   * The emptiness probe is deliberately wider than that action's `postalCode isnull`: a half-typed
   * address (street and city entered, postcode still to come) is user input too.
   */
  #prefillAddressTarget(rootField: FormlyFieldConfig | undefined, value: KvkValue): void {
    const target = this.props['kvkAddressTarget'];
    if (typeof target !== 'string' || target.trim() === '' || !rootField?.formControl) {
      return;
    }

    const source = (value?.['address'] ?? {}) as Record<string, unknown>;
    if (typeof source['postalCode'] !== 'string' || source['postalCode'].trim() === '') {
      return;
    }

    const path = target.trim().replace(/^entry\./, '');
    // getRawValue, not value: FormGroup.value omits disabled controls, so a permission-disabled
    // target address would read as empty and get overwritten — the exact thing this guards against.
    const current = this.#getValueAtPath(
      (rootField.formControl.getRawValue() ?? {}) as Record<string, unknown>,
      path
    ) as Record<string, unknown> | undefined;
    // Any filled part counts as "the user owns this address". Country alone does not: it is
    // routinely pre-set to a default and would block every prefill.
    const alreadyFilled = ['streetName', 'number', 'postalCode', 'city'].some(key => {
      const value = current?.[key];
      return typeof value === 'string' ? value.trim() !== '' : value !== null && value !== undefined && value !== '';
    });
    if (alreadyFilled) {
      return;
    }

    const address: Record<string, unknown> = { manual: false };
    for (const subKey of KVK_ADDRESS_SUB_KEYS) {
      if (source[subKey] !== undefined) {
        address[subKey] = source[subKey];
      }
    }

    // EntryPatchService rather than a plain patchValue so a target on another page still gets
    // marked dirty and survives the cross-page save sweep (#20789), same as the mapping result above.
    this.entryPatchService.applyEntryPatchToRootForm(
      rootField,
      UtilityService.setKeyValuePath({}, path, address) as Record<string, unknown>
    );
  }

  /**
   * Required validity: a valid KVK number must be present, and either the company was looked up
   * or manual entry is on (the sub-fields themselves stay optional).
   */
  readonly #kvkRequiredValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!this.props.required) {
      return null;
    }
    const value = (control.value ?? {}) as KvkValue;
    if (!this.validateKvkNumber((value.kvkNumber as string) ?? '')) {
      return { kvkRequired: true };
    }
    if (value.manual === true) {
      return null;
    }
    return value.lookedUp === true ? null : { kvkRequired: true };
  };

  /**
   * Deep-reads a dot-separated keyPath (with an optional leading `entry.`, matching the
   * `props.keyPath` format) from an apply-mapping result model.
   */
  #getValueAtPath(source: Record<string, unknown>, keyPath: string): unknown {
    const segments = keyPath.replace(/^entry\./, '').split('.');
    return segments.reduce<unknown>((current, segment) => {
      if (current !== null && typeof current === 'object') {
        return (current as Record<string, unknown>)[segment];
      }
      return undefined;
    }, source);
  }

  // --- Standard style helpers ----------------------------------------------

  private evaluateFormat(value: string | null) {
    // Any edit invalidates a previously successful lookup.
    this.lookupSucceeded.set(false);

    if (!value) {
      this.isValidKvkNumber.set(false);
      this.showWarning.set(false);
      return;
    }

    // Spaces and non-digit characters can never be valid → flag immediately.
    if (value.includes(' ')) {
      this.isValidKvkNumber.set(false);
      this.warningType.set('error');
      this.warningMessage.set('Voer het KVK-nummer in zonder spaties.');
      this.showWarning.set(true);
      return;
    }
    if (!/^\d*$/.test(value)) {
      this.isValidKvkNumber.set(false);
      this.warningType.set('error');
      this.warningMessage.set('Een KVK-nummer bestaat uit 8 cijfers (alleen cijfers).');
      this.showWarning.set(true);
      return;
    }
    // Too many digits can never become valid → flag immediately. Too few is just
    // an unfinished number, so stay silent until the field loses focus (onBlur).
    if (value.length > 8) {
      this.isValidKvkNumber.set(false);
      this.warningType.set('error');
      this.warningMessage.set('Een KVK-nummer bestaat uit 8 cijfers.');
      this.showWarning.set(true);
      return;
    }

    this.isValidKvkNumber.set(value.length === 8);
    this.showWarning.set(false);
  }

  private findKvkEndDate(obj: unknown): string | null {
    if (obj == null || typeof obj !== 'object') return null;
    for (const [key, value] of Object.entries(obj)) {
      if (/^kvk[-_]?(eind[-_]?datum|end[-_]?date)/i.test(key) && value) {
        return String(value);
      }
      if (value && typeof value === 'object') {
        const found = this.findKvkEndDate(value);
        if (found) return found;
      }
    }
    return null;
  }

  private formatDate(value: string): string {
    // KVK API levert YYYYMMDD (8 cijfers zonder separator); browser-Date parseert dat niet.
    const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(value);
    const date = compact ? new Date(`${compact[1]}-${compact[2]}-${compact[3]}`) : new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  /**
   * A KVK number is exactly eight digits and nothing else.
   *
   * Validate the value as typed (whitespace aside): stripping non-digits first would accept
   * 'AB12345678' and send it to the mapping, which then looks up a number the user never entered.
   */
  validateKvkNumber(value: string): boolean {
    return /^\d{8}$/.test((value ?? '').replace(/\s/g, ''));
  }
}
